// client/src/coms/components/KeywordTagInput.js
// Tag input for sticker keywords: type a word and press Enter, comma, space or Tab; Backspace on an empty
// field removes the last keyword; pasting "hi, hello hey" adds each word. Values are normalised the same
// way as on the server (lower case, single words).
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { normalizeKeyword } from '../stickers/stickerSuggest';

export const MAX_KEYWORDS = 20;

// ref.commit(): adds whatever is still typed in the field and returns the resulting list (used by Save).
const KeywordTagInput = forwardRef(function KeywordTagInput({ value = [], onChange, disabled = false, label = 'Keywords', placeholder = 'Add a keyword…', autoFocus = false }, ref) {
  const [text, setText] = useState('');
  const [hint, setHint] = useState('');
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    commit() {
      if (!text.trim()) return value;
      const next = add(text);
      setText('');
      return next;
    },
    focus() {
      if (inputRef.current) inputRef.current.focus();
    },
  }));

  function add(raw) {
    const parts = String(raw || '').split(/[\s,]+/).filter(Boolean);
    if (!parts.length) return value;
    const next = [...value];
    const problems = [];
    for (const p of parts) {
      const k = normalizeKeyword(p);
      if (!k) problems.push(`“${p.slice(0, 20)}” is not a single word`);
      else if (next.includes(k)) problems.push(`“${k}” is already there`);
      else if (next.length >= MAX_KEYWORDS) {
        problems.push(`At most ${MAX_KEYWORDS} keywords`);
        break;
      } else next.push(k);
    }
    setHint(problems.join(' · '));
    if (next.length !== value.length) onChange(next);
    return next;
  }

  function remove(k) {
    setHint('');
    onChange(value.filter((x) => x !== k));
    if (inputRef.current) inputRef.current.focus();
  }

  function onKeyDown(e) {
    const key = e.key;
    if ((key === 'Enter' || key === ',' || key === ' ' || (key === 'Tab' && text.trim())) && !e.nativeEvent.isComposing) {
      if (!text.trim()) {
        if (key !== 'Tab') e.preventDefault();
        return;
      }
      e.preventDefault();
      add(text);
      setText('');
    } else if (key === 'Backspace' && !text && value.length) {
      e.preventDefault();
      remove(value[value.length - 1]);
    }
  }

  return (
    <div>
      <div
        role="group"
        aria-label={label}
        onClick={() => inputRef.current && inputRef.current.focus()}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'center',
          minHeight: 38,
          padding: '5px 8px',
          borderRadius: 8,
          border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.14 * var(--vx-ov, 1)))',
          background: 'var(--vxs-1, #111214)',
          cursor: 'text',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {value.map((k) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 4px 2px 9px', borderRadius: 999, background: 'rgba(var(--vx-accent-rgb, 88,101,242), 0.25)', border: '1px solid rgba(var(--vx-accent-rgb, 88,101,242), 0.55)', fontSize: 12 }}>
            {k}
            {disabled ? null : (
              <button
                type="button"
                aria-label={`Remove keyword ${k}`}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(k);
                }}
                style={{ border: 0, background: 'transparent', color: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.7 * var(--vx-ov, 1)))', cursor: 'pointer', fontSize: 12, padding: '0 4px', lineHeight: 1 }}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {disabled ? null : (
          <input
            ref={inputRef}
            autoFocus={autoFocus}
            value={text}
            aria-label={`Add ${label.charAt(0).toLowerCase()}${label.slice(1)}`}
            placeholder={value.length ? '' : placeholder}
            onChange={(e) => {
              setText(e.target.value);
              if (hint) setHint('');
            }}
            onKeyDown={onKeyDown}
            onBlur={() => {
              if (text.trim()) {
                add(text);
                setText('');
              }
            }}
            onPaste={(e) => {
              const pasted = e.clipboardData && e.clipboardData.getData('text');
              if (pasted && /[\s,]/.test(pasted.trim())) {
                e.preventDefault();
                add(`${text} ${pasted}`);
                setText('');
              }
            }}
            maxLength={64}
            style={{ flex: 1, minWidth: 110, border: 0, outline: 'none', background: 'transparent', color: 'var(--vx-text, #fff)', fontSize: 13, padding: '3px 0' }}
          />
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, fontSize: 11, marginTop: 4, opacity: 0.75 }}>
        <span role="status" style={{ color: hint ? '#fca5a5' : undefined }}>
          {hint || (disabled ? '' : 'Press Enter or type a comma after each keyword.')}
        </span>
        <span style={{ marginLeft: 'auto' }}>
          {value.length}/{MAX_KEYWORDS}
        </span>
      </div>
    </div>
  );
});

export default KeywordTagInput;
